import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { trackEvent } from '../utils/analytics';
import './CustomerAcquisitionReport.css';

const now = new Date();
const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

const escapeCsv = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;

const CustomerAcquisitionReport = () => {
  const { t } = useLanguage();
  const [filters, setFilters] = useState({ startMonth: currentMonth, endMonth: currentMonth, scope: 'SELF' });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [customerSort, setCustomerSort] = useState({ field: 'createdAt', direction: 'desc' });

  const maxMonthlyCount = useMemo(() => Math.max(1, ...(report?.monthlySummary || []).map(item => item.count)), [report]);
  const maxCoachCount = useMemo(() => Math.max(1, ...(report?.coachSummary || []).map(item => item.count)), [report]);
  const monthCount = report?.monthlySummary?.length || 0;
  const monthlyAverage = monthCount ? (report.totalCustomers / monthCount).toFixed(1) : '0.0';
  const leadingCoach = report?.coachSummary?.find(item => item.count > 0);
  const sortedCustomers = useMemo(() => {
    if (!report?.customers) return [];

    const valueFor = (customer) => {
      if (customerSort.field === 'contact') return `${customer.email || ''} ${customer.phone || ''}`.trim();
      return customer[customerSort.field];
    };

    return report.customers.map((customer, index) => ({ customer, index })).sort((left, right) => {
      const leftValue = valueFor(left.customer);
      const rightValue = valueFor(right.customer);
      const leftMissing = leftValue === null || leftValue === undefined || leftValue === '';
      const rightMissing = rightValue === null || rightValue === undefined || rightValue === '';

      if (leftMissing !== rightMissing) return leftMissing ? 1 : -1;
      if (leftMissing) return left.index - right.index;

      let comparison;
      if (customerSort.field === 'createdAt') {
        comparison = new Date(leftValue).getTime() - new Date(rightValue).getTime();
      } else {
        comparison = String(leftValue).localeCompare(String(rightValue), undefined, { numeric: true, sensitivity: 'base' });
      }

      return comparison === 0
        ? left.index - right.index
        : comparison * (customerSort.direction === 'asc' ? 1 : -1);
    }).map(item => item.customer);
  }, [report, customerSort]);

  const updateCustomerSort = (field) => {
    setCustomerSort(current => ({
      field,
      direction: current.field === field ? (current.direction === 'asc' ? 'desc' : 'asc') : (field === 'createdAt' ? 'desc' : 'asc')
    }));
  };

  const sortableHeader = (label, field) => {
    const active = customerSort.field === field;
    const ariaSort = active ? (customerSort.direction === 'asc' ? 'ascending' : 'descending') : 'none';
    return (
      <th aria-sort={ariaSort}>
        <button type="button" className={active ? 'is-active' : ''} onClick={() => updateCustomerSort(field)}>
          {t(label)} <span className="acquisition-sort-icon" aria-hidden="true">{active ? (customerSort.direction === 'asc' ? '▲' : '▼') : '⇅'}</span>
        </button>
      </th>
    );
  };

  const generateReport = async (event) => {
    event.preventDefault();
    if (filters.startMonth > filters.endMonth) {
      setError(t('Start month cannot be after end month'));
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await axios.get('/api/reports/customer-acquisition', { params: filters });
      setReport(response.data);
      setCustomerSort({ field: 'createdAt', direction: 'desc' });
      trackEvent('report_generated', {
        event_category: 'Reports',
        event_label: 'Customer Acquisition Report',
        scope: filters.scope,
        records_count: response.data?.totalCustomers || 0
      });
    } catch (requestError) {
      console.error('Failed to generate customer acquisition report:', requestError);
      setError(t('Failed to generate customer acquisition report. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = () => {
    if (!report?.customers?.length) return;
    const headers = ['Joined Date', 'Customer', 'Email', 'Phone', 'Customer Type', 'Program', 'Status', 'Coach'];
    const rows = sortedCustomers.map(customer => [
      customer.createdAt ? new Date(customer.createdAt).toLocaleDateString('en-GB') : '',
      customer.customerName,
      customer.email,
      customer.phone,
      customer.customerType,
      customer.program,
      customer.status,
      customer.coachName
    ].map(escapeCsv).join(','));
    const blob = new Blob([[headers.join(','), ...rows].join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `customer_acquisition_${report.startMonth}_to_${report.endMonth}_${report.scope.toLowerCase()}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="acquisition-report">
      <div className="acquisition-report__heading">
        <div>
          <h3>{t('Customer Acquisition Report')}</h3>
          <p>{t('Track new customers by month and coach ownership.')}</p>
        </div>
      </div>

      <form className="acquisition-filters" onSubmit={generateReport}>
        <fieldset className="acquisition-scope">
          <legend>{t('Report Scope')}</legend>
          {['SELF', 'ORG'].map(scope => (
            <button
              key={scope}
              type="button"
              className={filters.scope === scope ? 'is-active' : ''}
              aria-pressed={filters.scope === scope}
              onClick={() => setFilters(current => ({ ...current, scope }))}
              disabled={loading}
            >
              <strong>{t(scope === 'SELF' ? 'Self' : 'Org')}</strong>
              <span>{t(scope === 'SELF' ? 'My customers (active subscription)' : "Customers of active descendant coaches")}</span>
            </button>
          ))}
        </fieldset>

        <label>
          <span>{t('From Month')}</span>
          <input type="month" value={filters.startMonth} max={filters.endMonth} onChange={event => setFilters(current => ({ ...current, startMonth: event.target.value }))} required disabled={loading} />
        </label>
        <label>
          <span>{t('To Month')}</span>
          <input type="month" value={filters.endMonth} min={filters.startMonth} onChange={event => setFilters(current => ({ ...current, endMonth: event.target.value }))} required disabled={loading} />
        </label>
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? t('Generating...') : t('Generate Report')}
        </button>
      </form>

      {error && <div className="alert alert-error">{error}</div>}

      {report && (
        <div className="acquisition-results">
          <div className="acquisition-kpis">
            <div><span>{t('New Customers')}</span><strong>{report.totalCustomers}</strong></div>
            <div className="acquisition-kpi--online"><span>{t('Online Clients')}</span><strong>{report.onlineCustomers || 0}</strong></div>
            <div className="acquisition-kpi--club"><span>{t('Club Clients')}</span><strong>{report.clubCustomers || 0}</strong></div>
            <div className="acquisition-kpi--unspecified"><span>{t('Type Not Set')}</span><strong>{report.unspecifiedCustomers || 0}</strong></div>
          </div>

          <div className="acquisition-context">
            <span>{t('Monthly Average')}: <strong>{monthlyAverage}</strong></span>
            <span>{t('Selected Period')}: <strong>{report.startMonth} → {report.endMonth}</strong></span>
            {report.scope === 'ORG' && <span>{t('Leading Coach')}: <strong>{leadingCoach?.coachName || '—'}</strong></span>}
          </div>

          <div className={`acquisition-visuals ${report.scope === 'SELF' ? 'acquisition-visuals--single' : ''}`}>
            <section className="acquisition-panel">
              <div className="acquisition-panel__title">
                <div><h4>{t('Monthly Acquisition Trend')}</h4><p>{t('New customers created in each selected month')}</p></div>
                <div className="acquisition-legend" aria-label={t('Customer type legend')}>
                  <span><i className="is-online" />{t('Online')}</span><span><i className="is-club" />{t('Club')}</span><span><i className="is-unspecified" />{t('Not Set')}</span>
                </div>
              </div>
              <div className="acquisition-columns" role="img" aria-label={t('Monthly customer acquisition bar chart')}>
                {report.monthlySummary.map(month => (
                  <div className="acquisition-column" key={month.month} title={`${month.monthLabel}: ${month.count} (${t('Online')}: ${month.onlineCount}, ${t('Club')}: ${month.clubCount}, ${t('Not Set')}: ${month.unspecifiedCount})`}>
                    <strong>{month.count}</strong>
                    <div className="acquisition-column__track">
                      <div className="acquisition-column__stack" style={{ height: `${Math.max(month.count ? 8 : 0, (month.count / maxMonthlyCount) * 100)}%` }}>
                        <i className="is-unspecified" style={{ height: `${month.count ? (month.unspecifiedCount / month.count) * 100 : 0}%` }} />
                        <i className="is-club" style={{ height: `${month.count ? (month.clubCount / month.count) * 100 : 0}%` }} />
                        <i className="is-online" style={{ height: `${month.count ? (month.onlineCount / month.count) * 100 : 0}%` }} />
                      </div>
                    </div>
                    <span>{month.monthLabel}</span>
                    <small><b className="is-online">{month.onlineCount}</b><b className="is-club">{month.clubCount}</b><b className="is-unspecified">{month.unspecifiedCount}</b></small>
                  </div>
                ))}
              </div>
            </section>

            {report.scope === 'ORG' && (
              <section className="acquisition-panel">
                <div className="acquisition-panel__title">
                  <div><h4>{t('Acquisition by Coach')}</h4><p>{t('Contribution across the complete coach hierarchy')}</p></div>
                  <div className="acquisition-legend" aria-label={t('Customer type legend')}>
                    <span><i className="is-online" />{t('Online')}</span><span><i className="is-club" />{t('Club')}</span><span><i className="is-unspecified" />{t('Not Set')}</span>
                  </div>
                </div>
                <div className="acquisition-coaches">
                  {report.coachSummary.length === 0 ? (
                    <div className="acquisition-empty">{t('No descendant coaches found.')}</div>
                  ) : report.coachSummary.map(coach => (
                    <div className="acquisition-coach" key={coach.coachId}>
                      <div>
                        <span>{coach.coachName}</span>
                        <strong>{coach.count} <small>({t('Online')} {coach.onlineCount} · {t('Club')} {coach.clubCount} · {t('Not Set')} {coach.unspecifiedCount})</small></strong>
                      </div>
                      <div className="acquisition-coach__track">
                        <i className="is-online" style={{ width: `${(coach.onlineCount / maxCoachCount) * 100}%` }} />
                        <i className="is-club" style={{ width: `${(coach.clubCount / maxCoachCount) * 100}%` }} />
                        <i className="is-unspecified" style={{ width: `${(coach.unspecifiedCount / maxCoachCount) * 100}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>

          <section className="acquisition-panel acquisition-table-panel">
            <div className="acquisition-panel__title acquisition-panel__title--table">
              <div><h4>{t('New Customer Details')}</h4><p>{t('{count} customers in the selected period', { count: report.totalCustomers })}</p></div>
              <button type="button" className="btn btn-secondary" onClick={exportCsv} disabled={!report.customers.length}>{t('Export CSV')}</button>
            </div>
            {report.customers.length === 0 ? (
              <div className="acquisition-empty">{t('No new customers found for the selected period and scope.')}</div>
            ) : (
              <div className="acquisition-table-wrap">
                <table>
                  <thead><tr>
                    {sortableHeader('Joined', 'createdAt')}
                    {sortableHeader('Customer', 'customerName')}
                    {sortableHeader('Contact', 'contact')}
                    {sortableHeader('Type', 'customerType')}
                    {sortableHeader('Program', 'program')}
                    {sortableHeader('Status', 'status')}
                    {report.scope === 'ORG' && sortableHeader('Coach', 'coachName')}
                  </tr></thead>
                  <tbody>{sortedCustomers.map(customer => (
                    <tr key={customer.customerId}>
                      <td>{customer.createdAt ? new Date(customer.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</td>
                      <td><a href={`/customers/${customer.customerId}`}>{customer.customerName}</a></td>
                      <td><span>{customer.email || '—'}</span><small>{customer.phone || '—'}</small></td>
                      <td>{customer.customerType ? t(customer.customerType) : '—'}</td>
                      <td>{customer.program || '—'}</td>
                      <td><span className={`acquisition-status acquisition-status--${String(customer.status || '').toLowerCase()}`}>{customer.status || '—'}</span></td>
                      {report.scope === 'ORG' && <td>{customer.coachName}</td>}
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
};

export default CustomerAcquisitionReport;
